import { Box, Palette, Search, Server, Zap } from "lucide-react";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useAppStore } from "../../store/useAppStore";
import { Button } from "../ui/button";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../ui/command";

// Static destinations (the working nav). Project entries are appended from the
// store below so you can jump straight to any project.
const PAGES = [
  { label: "Projects", icon: Box, path: "/projects" },
  { label: "Demo Deploy", icon: Zap, path: "/demo-deploy" },
  { label: "Design System", icon: Palette, path: "/design-system" },
];

/**
 * Global command palette.
 *
 * The visible search bar was removed from the app chrome, but the palette
 * itself is still worth having — it's the fastest way to reach a project or a
 * service. `showTrigger={false}` mounts it for the ⌘K / Ctrl+K shortcut alone,
 * with no button of its own.
 */
export function CommandPalette({
  showTrigger = true,
}: {
  showTrigger?: boolean;
} = {}) {
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();
  const { projects } = useAppStore();

  // Global ⌘K / Ctrl+K toggles the palette from anywhere.
  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  // Flatten every project's services into jump targets (carrying the project
  // name so it can be shown + searched alongside the service name).
  const services = React.useMemo(
    () =>
      projects.flatMap((project) =>
        (project.services ?? []).map((service) => ({
          id: service.id,
          name: service.name,
          projectId: project.id,
          projectName: project.name,
        }))
      ),
    [projects]
  );

  const go = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  return (
    <>
      {showTrigger && (
        <Button
          variant="outline"
          onClick={() => setOpen(true)}
          aria-label="Search"
          className="h-9 w-full justify-start gap-2 rounded-full border-border/50 bg-muted/30 px-3.5 text-sm text-muted-foreground hover:bg-muted/60 hover:text-foreground"
        >
          <Search className="h-3.5 w-3.5 shrink-0" />
          <span className="hidden sm:inline">Search…</span>
          <kbd className="pointer-events-none ml-auto hidden select-none items-center rounded border border-border/50 bg-background/60 px-1.5 py-0.5 font-mono text-2xs font-medium sm:inline-flex">
            ⌘K
          </kbd>
        </Button>
      )}

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder="Search projects, pages…" />
        <CommandList>
          <CommandEmpty>No results found.</CommandEmpty>

          <CommandGroup heading="Pages">
            {PAGES.map((page) => (
              <CommandItem
                key={page.path}
                value={page.label}
                onSelect={() => go(page.path)}
              >
                <page.icon className="h-4 w-4" />
                <span>{page.label}</span>
              </CommandItem>
            ))}
          </CommandGroup>

          {projects.length > 0 && (
            <CommandGroup heading="Projects">
              {projects.map((project) => (
                <CommandItem
                  key={project.id}
                  // value drives cmdk's fuzzy match — use the name so typing finds it.
                  value={`project ${project.name}`}
                  onSelect={() => go(`/projects/${project.id}`)}
                >
                  <Box className="h-4 w-4" />
                  <span>{project.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {services.length > 0 && (
            <CommandGroup heading="Services">
              {services.map((service) => (
                <CommandItem
                  key={service.id}
                  // Search matches both the service name and its project.
                  value={`service ${service.name} ${service.projectName}`}
                  onSelect={() =>
                    go(`/projects/${service.projectId}/services/${service.id}`)
                  }
                >
                  <Server className="h-4 w-4" />
                  <span>{service.name}</span>
                  <span className="ml-auto text-xs text-muted-foreground">
                    {service.projectName}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
      </CommandDialog>
    </>
  );
}
